import * as DialogPrimitive from "@radix-ui/react-dialog";
import { IconX } from "@tabler/icons-react";
import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

/**
 * Reemplazo de `<Modal>` de Mantine (Radix UI) · overlay 55% + blur (mismos
 * `overlayProps` del tema), radius-md, shadow-lg, transición "pop" (scale+
 * fade, ver `globals.css` · Etapa 2). A diferencia de Popover/DropdownMenu/
 * Select, el Dialog SÍ puede animar `transform` sin riesgo — no usa Popper,
 * se centra con un translate propio (ver comentario en `globals.css`).
 */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

export const DialogOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[1000] bg-black/55 backdrop-blur-[4px]",
      "data-[state=open]:animate-overlay-in data-[state=closed]:animate-overlay-out",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = "DialogOverlay";

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** El botón "×" flotante se puede ocultar si el footer ya trae un "Cancelar". */
  showClose?: boolean;
}

export const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, showClose = true, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-1/2 top-1/2 z-[1001] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2",
        "rounded-md border border-border bg-card p-6 text-card-foreground shadow-lg outline-none",
        // Contenido largo (p. ej. la consulta de una OC con muchas posiciones): el modal no
        // pasa del alto de la ventana y su contenido se desplaza con scrollbar. Los popovers
        // internos (Select, DatePicker, menús) van en portal, así que no se recortan.
        "max-h-[calc(100dvh-2rem)] overflow-y-auto scrollbar-suave",
        "data-[state=open]:animate-dialog-in data-[state=closed]:animate-dialog-out",
        className
      )}
      {...props}
    >
      {children}
      {showClose && (
        <DialogPrimitive.Close
          className={cn(
            "absolute right-4 top-4 rounded-sm text-muted-foreground opacity-70 transition-opacity",
            // Anillo SOLO con teclado (focus-visible): Radix enfoca la «×» al abrir el modal
            // y con `focus:` se veía un borde naranja permanente al abrirlo con el mouse.
            "hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-multiva-600/30"
          )}
        >
          <IconX size={16} />
          <span className="sr-only">Cerrar</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = "DialogContent";

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("mb-4 flex flex-col gap-1 pr-6", className)} {...props} />;
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("mt-6 flex justify-end gap-2", className)} {...props} />;
}

export const DialogTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
));
DialogTitle.displayName = "DialogTitle";

export const DialogDescription = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";
